import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { orderService } from '../../services/orderService';


import ComponentLibrary from './components/ComponentLibrary';
import CanvasWorkspace from './components/CanvasWorkspace';
import PropertyPanel from './components/PropertyPanel';
import LayersPanel from './components/LayersPanel';
import TopToolbar from './components/TopToolbar';
import PageManager from './components/PageManager';
import AISuggestionsPanel from './components/AISuggestionsPanel';
import { Home, Layers, FileText, Sparkles, ChevronLeft, ChevronRight, WifiOff } from 'lucide-react';
import AIChatPanel from './components/AIChatPanel';

export default function VisualBuilder() {
  const { user } = useAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [canvasComponents, setCanvasComponents] = useState([]);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activePanel, setActivePanel] = useState('components');
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(true);
  
  // Add missing state variables
  const [viewMode, setViewMode] = useState('desktop');
  const [zoom, setZoom] = useState(100);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedElements, setSelectedElements] = useState([]);
  const [activeRightPanel, setActiveRightPanel] = useState('layers');
  const [activeProject, setActiveProject] = useState(null);

  // Add this missing components array
  const components = [
    { id: 'button', name: 'Button', type: 'button' },
    { id: 'text', name: 'Text', type: 'text' },
    { id: 'image', name: 'Image', type: 'image' },
    { id: 'container', name: 'Container', type: 'container' }
  ];

  const [pages, setPages] = useState([
    { id: 'home', name: 'Home', icon: 'Home' },
    { id: 'about', name: 'About', icon: 'Info' }
  ]);
  const [activePage, setActivePage] = useState('home');

  const { isConnected, queueStatus, sync } = useConnectionStatus();
  const [showOfflineWarning, setShowOfflineWarning] = useState(false);

  // Real-time subscription for current project updates
  useRealtimeSubscription({
    table: 'orders',
    event: 'UPDATE',
    filter: currentProject?.id ? { column: 'id', value: currentProject?.id } : null,
    enabled: !!currentProject?.id,
    onUpdate: (updatedOrder) => {
      // Only update if change came from another session
      if (!isSaving) {
        setCurrentProject(prev => ({
          ...prev,
          projectData: updatedOrder?.projectData,
          name: updatedOrder?.projectName,
          description: updatedOrder?.projectDescription,
          lastModified: updatedOrder?.updatedAt
        }));
        setLastSaved(new Date(updatedOrder.updatedAt));
      }
    }
  });

  // Save project changes
  const saveProject = async (projectData) => {
    if (!currentProject?.id) return;

    try {
      setIsSaving(true);
      await orderService?.updateOrder(currentProject?.id, {
        projectData: projectData,
        projectName: currentProject?.name,
        projectDescription: currentProject?.description
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save project:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Show offline warning when connection is lost
  useEffect(() => {
    if (!isConnected) {
      setShowOfflineWarning(true);
    } else {
      setShowOfflineWarning(false);
    }
  }, [isConnected]);

  // Auto-save with offline support
  const handleAutoSave = async (changes) => {
    try {
      await orderService.updateOrder(activeProject?.id, changes);
      
      if (!isConnected) {
        // Show queued message
        console.log('Changes queued for sync');
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const handleDragStart = (e, component) => {
    e?.dataTransfer?.setData('component', JSON.stringify(component));
  };

  // Add handleDragOver function
  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = (component) => {
    const newComponents = [...canvasComponents, component];
    setCanvasComponents(newComponents);
    updateHistory(newComponents);
  };

  const handleSelectComponent = (component) => {
    setSelectedComponent(component);
    setSelectedElements([component]);
  };

  // Add handleElementClick function
  const handleElementClick = (element) => {
    setSelectedElements([element]);
    setSelectedComponent(element);
  };

  const handleUpdateComponent = (updatedComponent) => {
    const newComponents = canvasComponents?.map(comp =>
      comp?.id === updatedComponent?.id ? updatedComponent : comp
    );
    setCanvasComponents(newComponents);
    updateHistory(newComponents);
  };

  // Add handlePropertyChange function
  const handlePropertyChange = (property, value) => {
    if (selectedComponent) {
      const updatedComponent = { ...selectedComponent, [property]: value };
      handleUpdateComponent(updatedComponent);
    }
  };

  const handleDeleteComponent = (componentId) => {
    const newComponents = canvasComponents?.filter(comp => comp?.id !== componentId);
    setCanvasComponents(newComponents);
    updateHistory(newComponents);
    if (selectedComponent?.id === componentId) {
      setSelectedComponent(null);
    }
  };

  // Add handleDeleteElements function
  const handleDeleteElements = () => {
    if (selectedElements?.length > 0) {
      selectedElements?.forEach(element => {
        handleDeleteComponent(element?.id);
      });
      setSelectedElements([]);
    }
  };

  const handleToggleVisibility = (componentId) => {
    const newComponents = canvasComponents?.map(comp =>
      comp?.id === componentId ? { ...comp, visible: !comp?.visible } : comp
    );
    setCanvasComponents(newComponents);
    updateHistory(newComponents);
  };

  const updateHistory = (newComponents) => {
    const newHistory = history?.slice(0, historyIndex + 1);
    newHistory?.push(newComponents);
    setHistory(newHistory);
    setHistoryIndex(newHistory?.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCanvasComponents(history?.[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history?.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCanvasComponents(history?.[historyIndex + 1]);
    }
  };

  const handleSave = async () => {
    localStorage.setItem(`builder-${activePage}`, JSON.stringify(canvasComponents));
    return new Promise(resolve => setTimeout(resolve, 500));
  };

  const handlePreview = () => {
    window.open('/preview', '_blank');
  };

  // Add handlePublish function
  const handlePublish = async () => {
    await handleSave();
    console.log('Publishing project...');
    setShowPreview(false);
  };

  // Add handleCreateProject function
  const handleCreateProject = () => {
    console.log('Creating new project...');
  };

  // Add handleSwitchProject function
  const handleSwitchProject = (projectId) => {
    console.log('Switching to project:', projectId);
  };

  const handleAddPage = (pageName) => {
    const newPage = {
      id: `page-${Date.now()}`,
      name: pageName,
      icon: 'FileText'
    };
    setPages([...pages, newPage]);
    setActivePage(newPage?.id);
  };

  const handleDeletePage = (pageId) => {
    if (pages?.length > 1) {
      const newPages = pages?.filter(p => p?.id !== pageId);
      setPages(newPages);
      if (activePage === pageId) {
        setActivePage(newPages?.[0]?.id);
      }
      localStorage.removeItem(`builder-${pageId}`);
    }
  };

  const handleRenamePage = (pageId, newName) => {
    setPages(pages?.map(p => p?.id === pageId ? { ...p, name: newName } : p));
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handleAICodeGenerated = (code) => {
    // Here you could parse the code and add components to the canvas
    // For now, just show a notification
    console.log('AI Generated Code:', code);
    // You could implement code parsing and component addition here
  };

  const handleAddSuggestedComponent = (component) => {
    handleDrop(component);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Sidebar - FIXED WIDTH */}
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        user={user}
        activeProject={activeProject}
        onCreateProject={handleCreateProject}
        onSwitchProject={handleSwitchProject}
        onLogout={handleLogout}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      {/* Main Content - CONSTRAINED */}
      <div className="flex-1 flex flex-col min-w-0 max-w-full">
        {/* Top Toolbar - FIXED HEIGHT */}
        <div className="flex-shrink-0">
          <TopToolbar
            viewMode={viewMode}
            setViewMode={setViewMode}
            zoom={zoom}
            setZoom={setZoom}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onSave={handleSave}
            onPreview={() => setShowPreview(true)}
            onPublish={handlePublish}
            canUndo={historyIndex > 0}
            canRedo={historyIndex < history?.length - 1}
          />
        </div>

        {/* Offline Warning Banner */}
        {showOfflineWarning && (
          <div className="bg-yellow-900 border-b border-yellow-800">
            <div className="px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <WifiOff className="w-4 h-4 text-yellow-300" />
                <p className="text-sm text-yellow-100">
                  Working offline - Changes saved locally
                </p>
              </div>
              {queueStatus?.total > 0 && (
                <span className="text-xs text-yellow-200">
                  {queueStatus?.total} pending sync
                </span>
              )}
            </div>
          </div>
        )}

        {/* Middle Content Area - STRICT WIDTH DISTRIBUTION */}
        <div className="flex-1 flex min-h-0 max-h-full overflow-hidden">
          {/* Component Library - FIXED 288px (72*4) */}
          <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white overflow-hidden">
            <ComponentLibrary
              components={components}
              onDragStart={handleDragStart}
            />
          </div>

          {/* Canvas Workspace - FLEXIBLE CENTER */}
          <div className="flex-1 flex flex-col min-w-0 max-w-full bg-gray-100 overflow-hidden">
            {/* Page Manager - FIXED HEIGHT */}
            <div className="flex-shrink-0 border-b border-gray-200 bg-white">
              <PageManager
                pages={pages}
                activePage={activePage}
                onPageSelect={setActivePage}
                onSelectPage={setActivePage}
                onAddPage={handleAddPage}
                onDeletePage={handleDeletePage}
                onRenamePage={handleRenamePage}
              />
            </div>

            {/* Canvas - SCROLLABLE CONTENT */}
            <div className="flex-1 overflow-auto p-4 min-h-0">
              <CanvasWorkspace
                viewMode={viewMode}
                zoom={zoom}
                selectedElements={selectedElements}
                selectedComponent={selectedComponent}
                components={canvasComponents}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onElementClick={handleElementClick}
                onSelectComponent={handleSelectComponent}
                onDeleteElements={handleDeleteElements}
                showAISuggestions={showAISuggestions}
              />
            </div>
          </div>

          {/* AI Suggestions Panel - COLLAPSIBLE */}
          {showAISuggestions && (
            <div className="w-80 flex-shrink-0 overflow-hidden">
              <AISuggestionsPanel
                components={canvasComponents}
                onAddComponent={handleAddSuggestedComponent}
                pageContext={pages?.find(p => p?.id === activePage)?.name}
              />
            </div>
          )}

          {/* Right Panel - COLLAPSIBLE */}
          <div className={`flex-shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-hidden transition-all duration-250 ${
            isRightPanelCollapsed ? 'w-16' : 'w-80'
          }`}>
            {!isRightPanelCollapsed ? (
              <>
                {/* Tab Buttons - FIXED HEIGHT */}
                <div className="flex border-b border-gray-200 flex-shrink-0 h-12">
                  <button
                    type="button"
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      setActiveRightPanel('layers');
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeRightPanel === 'layers' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Layers
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      setActiveRightPanel('properties');
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeRightPanel === 'properties' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Properties
                  </button>
                </div>

                {/* Panel Content - SCROLLABLE */}
                <div className="flex-1 overflow-y-auto min-h-0">
                  {activeRightPanel === 'layers' ? (
                    <LayersPanel
                      elements={selectedElements}
                      components={canvasComponents}
                      selectedComponent={selectedComponent}
                      onSelectComponent={handleSelectComponent}
                      onReorder={(newOrder) => console.log('Reorder:', newOrder)}
                      onReorderComponents={(newOrder) => setCanvasComponents(newOrder)}
                      onToggleVisibility={handleToggleVisibility}
                      onDeleteComponent={handleDeleteComponent}
                    />
                  ) : (
                    <PropertyPanel
                      selectedElements={selectedElements}
                      selectedComponent={selectedComponent}
                      onPropertyChange={handlePropertyChange}
                      onUpdateComponent={handleUpdateComponent}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center py-4 space-y-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    setIsRightPanelCollapsed(false);
                    setActiveRightPanel('layers');
                  }}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  title="Layers"
                >
                  <Layers size={20} />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    setIsRightPanelCollapsed(false);
                    setActiveRightPanel('properties');
                  }}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  title="Properties"
                >
                  <FileText size={20} />
                </button>
              </div>
            )}

            {/* Collapse/Expand Button */}
            <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  setIsRightPanelCollapsed(!isRightPanelCollapsed);
                }}
                className="w-full flex items-center justify-center p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all duration-250"
                aria-label={isRightPanelCollapsed ? 'Expand right panel' : 'Collapse right panel'}
              >
                {isRightPanelCollapsed ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* AI Chat Panel */}
      <AIChatPanel 
        isOpen={isAIChatOpen}
        onClose={() => setIsAIChatOpen(false)}
        onCodeGenerated={handleAICodeGenerated}
      />
      {/* AI Suggestions Toggle Button */}
      <button
        type="button"
        onClick={(e) => {
          e?.preventDefault();
          e?.stopPropagation();
          setShowAISuggestions(!showAISuggestions);
        }}
        className={`fixed bottom-20 right-6 p-3 rounded-full shadow-lg transition-all transform hover:scale-110 z-50 ${
          showAISuggestions 
            ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-200 text-gray-600 hover:bg-gray-300'
        }`}
        aria-label={showAISuggestions ? 'Hide AI Suggestions' : 'Show AI Suggestions'}
      >
        <Sparkles className="w-5 h-5" />
      </button>
      {/* AI Chat Toggle Button */}
      {!isAIChatOpen && (
        <button
          type="button"
          onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            setIsAIChatOpen(true);
          }}
          className="fixed bottom-6 right-6 p-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full shadow-lg transition-all transform hover:scale-110 z-50"
          aria-label="Open AI Assistant"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}
      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Preview</h2>
            <div className="border border-gray-300 rounded-lg p-4 min-h-64 bg-gray-50">
              <p className="text-gray-600">This is a preview of your page. Click the button below to publish.</p>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  setShowPreview(false);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors font-medium"
              >
                Close
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  handlePublish();
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}